import ExpandableSection from '@risk-smart/themed-cloudscape-components/expandable-section';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { GetControlByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import { useGetTestResultsByControlId } from 'src/hooks/queries/test-result/useGetTestResultsByControlId';
import { useSmartCopyAmplitude } from 'src/hooks/smart-copy';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { getSmartCopyTemplateActionState } from 'src/utils/smart-copy';

import { useDeleteTestResults } from '@/hooks/mutations/test-result/useDeleteTestResults';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { getCounter } from '@/utils/collectionUtils';

import TestResultModal from '../TestResultModal';
import { CONTROL_TEST_SMART_COPY } from './controlTestSmartCopy';
import { useGetCollectionTableProps } from './performanceRatingConfig';
import type { PerformanceRegisterFields } from './types';

type Props = {
  control: GetControlByIdQuery['control'][number];
};

const PerformanceRatingRegister: FC<Props> = ({ control }) => {
  useI18NSummaryHelpContent('testResults.tabHelp');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'testResults',
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [selectedTestResults, setSelectedTestResults] = useState<
    PerformanceRegisterFields[]
  >([]);
  const {
    hasPermission: canDeleteTestResults,
    loading: canDeleteTestResultsLoading,
  } = useHasPermissionQuery('delete:test_result', control);
  const {
    hasPermission: canInsertTestResult,
    loading: canInsertTestResultLoading,
  } = useHasPermissionQuery('insert:test_result', control);

  const isSmartCopyEnabled = useIsFeatureFlagEnabled(
    CONTROL_TEST_SMART_COPY.flagKey
  );
  // Smart Copy events fired from this component are naturally gated by the
  // `isSmartCopyEnabled` render block below — the ActionsButton (and its
  // onOpen) and handleAddTestResultFromTemplate are unreachable when off.
  const { events, logEvent } = useSmartCopyAmplitude({
    entityLabel: CONTROL_TEST_SMART_COPY.eventLabel,
  });

  const shouldEnableMultiSelect =
    canDeleteTestResults ||
    canDeleteTestResultsLoading ||
    (isSmartCopyEnabled && (canInsertTestResult || canInsertTestResultLoading));

  const [openTestResultId, setOpenTestResultId] = useState<
    string | undefined
  >();
  const [copyFromTestResultId, setCopyFromTestResultId] = useState<
    string | undefined
  >();

  const { data, loading, refetch } = useGetTestResultsByControlId({
    queryArgs: { controlId: control.Id },
  });

  const handleTestResultModalOpen = () => {
    setOpenTestResultId(undefined);
    setCopyFromTestResultId(undefined);
    setIsEditOpen(true);
  };

  const handleAddTestResultFromTemplate = () => {
    const source = selectedTestResults[0];
    if (!source) {
      return;
    }
    logEvent(events.TEMPLATE_SELECTED, {
      controlId: control.Id,
      sourceResultId: source.Id,
    });
    setOpenTestResultId(undefined);
    setCopyFromTestResultId(source.Id);
    setIsEditOpen(true);
  };

  const handleTestResultModalClose = async (saved?: boolean) => {
    if (saved) {
      await refetch();
      setSelectedTestResults([]);
    }
    setOpenTestResultId(undefined);
    setCopyFromTestResultId(undefined);
    setIsEditOpen(false);
  };

  const { deleteTestResults, loading: deleteLoading } = useDeleteTestResults();

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    asyncAction: async () => {
      await deleteTestResults({
        ids: selectedTestResults.map((s) => s.Id),
      });
      await refetch();
      setSelectedTestResults([]);
      setIsDeleteModalVisible(false);

      return true;
    },
  });

  const tableProps = useGetCollectionTableProps(
    control,
    (testResult) => {
      setOpenTestResultId(testResult.Id);
      setIsEditOpen(true);
    },
    () => {
      setIsEditOpen(true);
    },
    data?.test_result
  );

  const templateActionState = getSmartCopyTemplateActionState(t, {
    entityNoun: CONTROL_TEST_SMART_COPY.entityNoun,
    selectedCount: selectedTestResults.length,
  });

  return (
    <>
      <SpaceBetween size={'m'}>
        <TabHeader
          actions={
            <SpaceBetween direction={'horizontal'} size={'xs'}>
              <Permission
                permission={'delete:test_result'}
                parentObject={control}
              >
                <Button
                  formAction={'none'}
                  variant={'normal'}
                  disabled={!selectedTestResults.length}
                  onClick={() => setIsDeleteModalVisible(true)}
                >
                  {t('delete')}
                </Button>
              </Permission>
              <Permission
                permission={'insert:test_result'}
                parentObject={control}
              >
                {isSmartCopyEnabled ? (
                  <ActionsButton
                    onOpen={() =>
                      logEvent(events.DROPDOWN_OPENED, {
                        controlId: control.Id,
                      })
                    }
                    buttonText={st('actions_button')}
                    testId={'testResultActions'}
                    variant={'primary'}
                    items={[
                      {
                        id: 'addTestResult',
                        text: st('add_test_result_new'),
                        onItemClick: handleTestResultModalOpen,
                      },
                      {
                        id: 'addTestResultFromTemplate',
                        text: st('add_test_result_from_template'),
                        ...templateActionState,
                        onItemClick: handleAddTestResultFromTemplate,
                      },
                    ]}
                  />
                ) : (
                  <Button
                    variant={'primary'}
                    formAction={'none'}
                    onClick={handleTestResultModalOpen}
                  >
                    {st('add_button')}
                  </Button>
                )}
              </Permission>
            </SpaceBetween>
          }
        >
          {st('tab_title')}
        </TabHeader>
      </SpaceBetween>

      <ExpandableSection
        header={
          <div className={'flex space-x-2'}>
            <span>{st('performanceRatingSubheading')}</span>
            <span className={'text-grey font-normal'}>
              {getCounter(tableProps.totalItemsCount ?? 0, loading)}
            </span>
          </div>
        }
        defaultExpanded={true}
      >
        <Table
          {...tableProps}
          selectionType={shouldEnableMultiSelect ? 'multi' : undefined}
          selectedItems={selectedTestResults}
          trackBy={'Id'}
          onSelectionChange={({ detail }) => {
            setSelectedTestResults(detail.selectedItems);
          }}
          resizableColumns={true}
          variant={'embedded'}
          loading={loading}
          loadingText={t('loadingTestResults') ?? ''}
          sortingDisabled={false}
        />
      </ExpandableSection>
      {isEditOpen && control.Id && (
        <TestResultModal
          parentControlId={control.Id}
          Id={openTestResultId}
          sourceResultId={copyFromTestResultId}
          onDismiss={handleTestResultModalClose}
          assessmentMode={'rating'}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => {
          refetch();
          setOpenTestResultId(undefined);
          setSelectedTestResults([]);
          setIsDeleteModalVisible(false);
        }}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default PerformanceRatingRegister;
